// =============================================================================
// Realtime - Mappa topic → query da invalidare
// =============================================================================
// Il server non manda dati, manda il nome dell'area che e' cambiata. Qui
// quell'area diventa un elenco di prefissi di query key: React Query rifetcha
// solo le query *attive* (montate) che ci ricadono, quindi una modifica altrui
// costa esattamente le richieste che servono a ridisegnare quello che l'utente
// ha davvero davanti.
//
// I prefissi devono restare allineati a `queryKeys` in api/hooks.ts.
// =============================================================================

export const REALTIME_TOPICS = [
  'appointments',
  'resources',
  'resourceTypes',
  'availability',
  'absenceReasons',
  'calendarPresets',
  'services',
  'customers',
  'bookingRequests',
  'tenant',
  'members',
] as const;

export type RealtimeTopic = (typeof REALTIME_TOPICS)[number];

/** Query key (prefisso) da invalidare per ciascun topic. */
const TOPIC_QUERY_KEYS: Record<RealtimeTopic, readonly (readonly unknown[])[]> = {
  // Un appuntamento spostato cambia il calendario, gli slot liberi e ogni
  // aggregato che li conta.
  appointments: [
    ['appointments'],
    ['availableSlots'],
    ['customers'],
    ['dashboardStats'],
    ['dashboardUtilization'],
    ['dashboardReorganization'],
    ['reportsUtilization'],
    ['reportsPeakHours'],
  ],
  resources: [
    ['resources'],
    ['resource'],
    ['appointments'],
    ['availableSlots'],
    ['dashboardUtilization'],
    ['reportsUtilization'],
  ],
  resourceTypes: [['resourceTypes'], ['resourceTypeUsage'], ['resources']],
  // Orari e assenze non cambiano gli appuntamenti esistenti, ma cambiano le
  // fasce disegnate nella griglia e gli slot proponibili.
  availability: [['resource'], ['resources'], ['availableSlots'], ['appointments']],
  absenceReasons: [['absence-reasons'], ['resource']],
  // Preset privati dell'utente: il segnale arriva a tutta l'organizzazione, ma
  // ogni client rifetcha solo i propri. Serve a tenere allineate le schede
  // aperte dallo stesso operatore.
  calendarPresets: [['calendar-presets']],
  services: [['services'], ['serviceCategories'], ['availableSlots']],
  customers: [['customers']],
  bookingRequests: [['booking-requests'], ['appointments'], ['availableSlots']],
  tenant: [['tenant-details'], ['tenant-deletion-summary'], ['timezones']],
  members: [['tenant-members'], ['tenant-invites']],
};

/**
 * Prefissi da invalidare per un insieme di topic, senza duplicati.
 * Le chiavi sono confrontate per identita' del primo segmento, che e' come
 * React Query fa il match di prefisso.
 */
export function queryKeysForTopics(topics: readonly string[]): (readonly unknown[])[] {
  const seen = new Set<string>();
  const keys: (readonly unknown[])[] = [];

  for (const topic of topics) {
    const topicKeys = TOPIC_QUERY_KEYS[topic as RealtimeTopic];
    if (!topicKeys) continue;

    for (const key of topicKeys) {
      const id = String(key[0]);
      if (seen.has(id)) continue;
      seen.add(id);
      keys.push(key);
    }
  }

  return keys;
}
